import {Equation} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/FlatEquationSolver';

export enum StoichiometryVariable {
  LIMITING_REACTANT_EQUIVALENCE = 'LIMITING_REACTANT_EQUIVALENCE',
  LIMITING_REACTANT_MOLS = 'LIMITING_REACTANT_MOLS',
  LIMITING_REACTANT_MOL_UNIT_CONVERSION = 'LIMITING_REACTANT_MOL_UNIT_CONVERSION',
  EQUIVALENCE = 'EQUIVALENCE',
  MOLECULE_PURITY = 'MOLECULE_PURITY',
  MOLECULAR_WEIGHT = 'MOLECULAR_WEIGHT',
  MOLS = 'MOLS',
  MOL_UNIT_CONVERSION = 'MOL_UNIT_CONVERSION',
  AMOUNT = 'AMOUNT',
  AMOUNT_UNIT_CONVERSION = 'AMOUNT_UNIT_CONVERSION',
}

export const LIMITING_REACTANT_VARIABLES = new Set([
  StoichiometryVariable.LIMITING_REACTANT_EQUIVALENCE,
  StoichiometryVariable.LIMITING_REACTANT_MOLS,
  StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
]);

const EQUIVALENCE_FROM_MOLS_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.EQUIVALENCE,
  deps: [
    StoichiometryVariable.MOLS,
    StoichiometryVariable.MOL_UNIT_CONVERSION,
    StoichiometryVariable.LIMITING_REACTANT_MOLS,
    StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.MOLS]: mols,
      [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
      [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolUnitConversion,
    } = args;
    const molsInBaseUnit = mols * molUnitConversion;
    const limitingReactantMolsInBaseUnit = limitingReactantMols * limitingReactantMolUnitConversion;
    return molsInBaseUnit / limitingReactantMolsInBaseUnit;
  },
};

const EQUIVALENCE_FROM_AMOUNT_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.EQUIVALENCE,
  deps: [
    StoichiometryVariable.AMOUNT,
    StoichiometryVariable.AMOUNT_UNIT_CONVERSION,
    StoichiometryVariable.MOLECULAR_WEIGHT,
    StoichiometryVariable.MOLECULE_PURITY,
    StoichiometryVariable.LIMITING_REACTANT_MOLS,
    StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.AMOUNT]: amount,
      [StoichiometryVariable.AMOUNT_UNIT_CONVERSION]: amountUnitConversion,
      [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight,
      [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
      [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolUnitConversion,
    } = args;
    const amountInGrams = amount * amountUnitConversion;
    // Calculate equivalence from amount:
    // effective_mols = (mass * purity) / molecular_weight
    // equivalence = effective_mols / limiting_reactant_mols
    const limitingReactantMolsInBaseUnit = limitingReactantMols * limitingReactantMolUnitConversion;
    return (amountInGrams * moleculePurity) / (molecularWeight * limitingReactantMolsInBaseUnit);
  },
};

export const MOLS_FROM_AMOUNT_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.MOLS,
  deps: [
    StoichiometryVariable.AMOUNT,
    StoichiometryVariable.AMOUNT_UNIT_CONVERSION,
    StoichiometryVariable.MOLECULAR_WEIGHT,
    StoichiometryVariable.MOLECULE_PURITY,
    StoichiometryVariable.MOL_UNIT_CONVERSION,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.AMOUNT]: amount,
      [StoichiometryVariable.AMOUNT_UNIT_CONVERSION]: amountUnitConversion,
      [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight,
      [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
    } = args;
    const amountInGrams = amount * amountUnitConversion;
    // Adjust for purity: pure_mols = total_mass * purity / MW
    const molsInBaseUnit = (amountInGrams * moleculePurity) / molecularWeight;
    return molsInBaseUnit / molUnitConversion;
  },
};

const MOLS_FROM_EQUIVALENCE_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.MOLS,
  deps: [
    StoichiometryVariable.EQUIVALENCE,
    StoichiometryVariable.LIMITING_REACTANT_MOLS,
    StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
    StoichiometryVariable.MOL_UNIT_CONVERSION,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.EQUIVALENCE]: equivalence,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
      [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolUnitConversion,
      [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
    } = args;
    const limitingReactantMolsInBaseUnit = limitingReactantMols * limitingReactantMolUnitConversion;
    const molsInBaseUnit = equivalence * limitingReactantMolsInBaseUnit;
    return molsInBaseUnit / molUnitConversion;
  },
};

const AMOUNT_FROM_MOLS_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.AMOUNT,
  deps: [
    StoichiometryVariable.MOLS,
    StoichiometryVariable.MOLECULAR_WEIGHT,
    StoichiometryVariable.MOLECULE_PURITY,
    StoichiometryVariable.MOL_UNIT_CONVERSION,
    StoichiometryVariable.AMOUNT_UNIT_CONVERSION,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.MOLS]: mols,
      [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight,
      [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
      [StoichiometryVariable.AMOUNT_UNIT_CONVERSION]: amountUnitConversion,
    } = args;
    const molsInBaseUnit = mols * molUnitConversion;
    const amountInGrams = molsInBaseUnit * molecularWeight;
    // Calculate required total mass accounting for impurity:
    // total_mass = pure_mols * MW / purity
    return amountInGrams / moleculePurity / amountUnitConversion;
  },
};

const AMOUNT_FROM_EQUIVALENCE_EQUATION: Equation<StoichiometryVariable> = {
  solution: StoichiometryVariable.AMOUNT,
  deps: [
    StoichiometryVariable.EQUIVALENCE,
    StoichiometryVariable.MOLECULAR_WEIGHT,
    StoichiometryVariable.MOLECULE_PURITY,
    StoichiometryVariable.LIMITING_REACTANT_MOLS,
    StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
    StoichiometryVariable.AMOUNT_UNIT_CONVERSION,
  ],
  calc: (args) => {
    const {
      [StoichiometryVariable.EQUIVALENCE]: equivalence,
      [StoichiometryVariable.MOLECULAR_WEIGHT]: molecularWeight,
      [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
      [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolUnitConversion,
      [StoichiometryVariable.AMOUNT_UNIT_CONVERSION]: amountUnitConversion,
    } = args;
    const limitingReactantMolsInBaseUnit = limitingReactantMols * limitingReactantMolUnitConversion;
    const amountInGrams = equivalence * molecularWeight * limitingReactantMolsInBaseUnit;
    return amountInGrams / moleculePurity / amountUnitConversion;
  },
};

export const STOICHIOMETRY_CALCULATION_EQUATIONS: Array<Equation<StoichiometryVariable>> = [
  EQUIVALENCE_FROM_MOLS_EQUATION,
  EQUIVALENCE_FROM_AMOUNT_EQUATION,
  MOLS_FROM_AMOUNT_EQUATION,
  MOLS_FROM_EQUIVALENCE_EQUATION,
  AMOUNT_FROM_MOLS_EQUATION,
  AMOUNT_FROM_EQUIVALENCE_EQUATION,
];
